// src/pages/Support.tsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
  Modal,
  Box,
  IconButton,
  Paper,
  Alert,
  Grid,
  Chip,
  Pagination,
} from '@mui/material';
import { Add, Visibility, Close } from '@mui/icons-material';
import api from '../api/api';

interface SupportTicket {
  id: string;
  short_description: string;
  detailed_description: string;
  status: string;
  created_at: string;
  telegram_message_id?: number | null;
  media_files?: string[] | null;
  media_info?: Array<{ filename: string; url: string }> | null;
}

interface ListSupportTicketResponse {
  tickets: SupportTicket[];
  total_count: number;
}

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 600 },
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: '12px',
  maxHeight: '90vh',
  overflow: 'auto',
};

const Support: React.FC = () => {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [totalTickets, setTotalTickets] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'error' | 'success' | 'info' } | null>(null);

  // Пагинация
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // Модалки
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);

  // Форма создания
  const [shortDescription, setShortDescription] = useState('');
  const [detailedDescription, setDetailedDescription] = useState('');
  const [mediaFiles, setMediaFiles] = useState<File[]>([]);
  const STATUS_LABELS: Record<string, string> = {
  new: 'Новый',
  in_progress: 'В процессе',
  closed: 'Закрыт',
};

  useEffect(() => {
    fetchTickets();
  }, [page]);

  const fetchTickets = async () => {
    setIsLoading(true);
    try {
      const response = await api.get<ListSupportTicketResponse>('/support/tickets', {
        params: { limit: pageSize, offset: (page - 1) * pageSize },
      });
      setTickets(response.data.tickets || []);
      setTotalTickets(response.data.total_count || 0);
      setMessage(null);
    } catch (error: any) {
      setMessage({ text: `Ошибка загрузки тикетов: ${error.response?.data?.detail || error.message}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const createTicket = async () => {
    if (!shortDescription.trim()) {
      setMessage({ text: 'Заполните краткое описание', type: 'error' });
      return;
    }

    setIsLoading(true);
    const formData = new FormData();
    formData.append('short_description', shortDescription.trim());
    if (detailedDescription.trim()) {
      formData.append('detailed_description', detailedDescription.trim());
    }
    mediaFiles.forEach((file) => {
      formData.append('media_files', file);
    });

    try {
      await api.post('/support/tickets', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMessage({ text: 'Тикет успешно создан!', type: 'success' });
      setOpenCreateModal(false);
      resetCreateForm();
      fetchTickets();
    } catch (error: any) {
      setMessage({ text: `Ошибка создания: ${error.response?.data?.detail || error.message}`, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const viewTicket = (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
    setOpenViewModal(true);
  };

  const resetCreateForm = () => {
    setShortDescription('');
    setDetailedDescription('');
    setMediaFiles([]);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      if (mediaFiles.length + newFiles.length > 10) {
        setMessage({ text: 'Максимум 10 файлов', type: 'error' });
        return;
      }
      setMediaFiles(prev => [...prev, ...newFiles]);
    }
  };

  const removeFile = (index: number) => {
    setMediaFiles(prev => prev.filter((_, i) => i !== index));
  };

  const totalPages = Math.ceil(totalTickets / pageSize);

  if (isLoading && tickets.length === 0) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography mt={2}>Загрузка тикетов...</Typography>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 6, maxWidth: 'lg' }}>
      {/* Описание */}
      <Box sx={{ bgcolor: '#F9FAFB', p: 4, borderRadius: 2, mb: 6 }}>
        <Typography variant="h6" sx={{ color: '#1E3A8A', mb: 3, fontWeight: 600 }}>
          Поддержка Typhon
        </Typography>
        <Typography sx={{ color: '#4B5563', mb: 3, lineHeight: 1.7 }}>
          Здесь вы можете создать тикет, если у вас возникли вопросы, проблемы с функционалом или нужна помощь с настройкой.
        </Typography>
        <Typography sx={{ color: '#4B5563', mb: 2 }}>
          • Краткое описание — обязательно (до 300 символов)<br />
          • Подробное описание — желательно (до 3000 символов)<br />
          • Фото/видео — до 10 файлов (скриншоты, видео ошибок и т.д.)<br />
          • Обязательно укажите id и тип автоматизации, по которой вы составляете запрос, имя аккаунтов, другую полезную информацию
        </Typography>
        <Typography sx={{ color: '#1E40AF', fontWeight: 500 }}>
          Наши специалисты свяжутся с вами в Telegram. Если вам предпочтителен другой канал связи с поддержкой, укажите это в тикете!
        </Typography>
      </Box>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', mb: 4 }}>
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" sx={{ color: '#1E3A8A', fontWeight: 600 }}>
            Мои тикеты поддержки
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpenCreateModal(true)}
            disabled={isLoading}
            sx={{ bgcolor: '#1E3A8A', '&:hover': { bgcolor: '#1E40AF' } }}
          >
            Создать тикет
          </Button>
        </Box>

        <Box sx={{ p: 3 }}>
          {tickets.length > 0 ? (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                    <TableCell sx={{ fontWeight: 600, color: '#1E3A8A' }}>Краткое описание</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1E3A8A' }}>Статус</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1E3A8A' }}>Создан</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#1E3A8A' }} align="right">Действия</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tickets.map(t => (
                    <TableRow key={t.id} sx={{ '&:hover': { bgcolor: '#F9FAFB' } }}>
                      <TableCell sx={{ maxWidth: 400 }}>
                        <Typography noWrap title={t.short_description}>
                          {t.short_description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={STATUS_LABELS[t.status] || t.status}
                          size="small"
                          color={
                            t.status === 'new' ? 'warning' :
                            t.status === 'closed' ? 'success' :
                            t.status === 'in_progress' ? "info" :
                                'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(t.created_at).toLocaleString('ru-RU')}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => viewTicket(t)}
                          disabled={isLoading}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, newPage) => setPage(newPage)}
                    color="primary"
                  />
                </Box>
              )}
            </>
          ) : (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              У вас пока нет тикетов. Создайте первый!
            </Typography>
          )}
        </Box>
      </Paper>

      {/* МОДАЛКА СОЗДАНИЯ ТИКЕТА */}
      <Modal open={openCreateModal} onClose={() => !isLoading && setOpenCreateModal(false)}>
        <Box sx={modalStyle}>
          <Typography variant="h6" sx={{ color: '#1E3A8A', mb: 3 }}>
            Создать тикет поддержки
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Краткое описание (до 300 символов)"
                value={shortDescription}
                onChange={e => setShortDescription(e.target.value)}
                fullWidth
                required
                inputProps={{ maxLength: 300 }}
                helperText={`${shortDescription.length}/300`}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                label="Подробное описание (опционально)"
                value={detailedDescription}
                onChange={e => setDetailedDescription(e.target.value)}
                fullWidth
                multiline
                rows={6}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{ py: 3, borderStyle: 'dashed' }}
              >
                {mediaFiles.length > 0
                  ? `Выбрано файлов: ${mediaFiles.length} (макс. 10)`
                  : 'Прикрепить фото/видео (макс. 10)'}
                <input
                  type="file"
                  hidden
                  multiple
                  accept="image/*,video/*"
                  onChange={handleFileChange}
                />
              </Button>
              {mediaFiles.length > 0 && (
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {mediaFiles.map((file, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        bgcolor: '#F3F4F6',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="caption">{file.name}</Typography>
                      <IconButton size="small" onClick={() => removeFile(idx)}>
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Button
                variant="contained"
                onClick={createTicket}
                disabled={isLoading || !shortDescription.trim()}
                fullWidth
                sx={{ py: 1.5 }}
              >
                {isLoading ? <CircularProgress size={20} /> : 'Отправить тикет'}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>

      {/* МОДАЛКА ПРОСМОТРА ТИКЕТА */}
      <Modal open={openViewModal} onClose={() => setOpenViewModal(false)}>
        <Box sx={modalStyle}>
          {selectedTicket && (
            <>
              <Typography variant="h6" sx={{ color: '#1E3A8A', mb: 2 }}>
                Тикет #{selectedTicket.id.slice(0, 8)}
              </Typography>

                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Статус: <Chip label={STATUS_LABELS[selectedTicket.status] || selectedTicket.status} size="small"
                                  color={
                                      selectedTicket.status === 'new' ? 'warning' :
                                          selectedTicket.status === 'closed' ? 'success' :
                                              selectedTicket.status === 'in_progress' ? "info" :
                                                  'default'
                                  }/>
                </Typography>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Создан: {new Date(selectedTicket.created_at).toLocaleString('ru-RU')}
              </Typography>

              <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>
                Краткое описание:
              </Typography>
              <Typography sx={{ mb: 3 }}>{selectedTicket.short_description}</Typography>

              {selectedTicket.detailed_description && (
                <>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                    Подробное описание:
                  </Typography>
                  <Typography sx={{ mb: 3, whiteSpace: 'pre-wrap' }}>
                    {selectedTicket.detailed_description}
                  </Typography>
                </>
              )}

                {selectedTicket.media_info && selectedTicket.media_info.length > 0 && (
                    <>
                        <Typography variant="subtitle1" sx={{mb: 1, fontWeight: 600}}>
                            Прикреплённые файлы:
                        </Typography>
                        <Grid container spacing={1}>
                            {selectedTicket.media_info.map((media, idx) => (
                                <Grid size={{xs: 12, sm: 6, md: 4}} key={idx}>
                                    <Box
                                        component="img"
                                        src={media.url}
                                        alt={media.filename}
                                        sx={{
                                            width: '100%',
                                            height: 140,
                                            objectFit: 'cover',
                                            borderRadius: 1,
                                            border: '1px solid #E5E7EB',
                                        }}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </>
                )}

              <Box sx={{ mt: 4, textAlign: 'right' }}>
                <Button variant="outlined" onClick={() => setOpenViewModal(false)}>
                  Закрыть
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </Container>
  );
};

export default Support;